import axios, { AxiosInstance } from 'axios'
import type { CheckoutShippingPayload } from './checkoutShipping'
import {
  buildAssessPhotoFormData,
  buildSuggestWithPhotoFormData,
  type AssessPhotoOptions,
  type MultipartPhotoFile,
  type PricingSuggestPhotoParams,
} from './pricingMultipart'
import {
  postPricingSuggestPhotoResilient,
  postPricingSuggestResilient,
  type PricingSuggestBody,
} from './pricingSuggestResilient'

/** Axios responses — intentionally loose so web + RN apps stay ergonomic */
type AxPromise = Promise<any>

export interface ReDevicerApiConfig {
  /** Full API root including `/api`, e.g. `http://127.0.0.1:8000/api` or `/api` */
  baseURL: string
}

export interface ReDevicerApi {
  /** Raw axios instance — use for platform-specific uploads / extensions */
  axios: AxiosInstance
  authAPI: {
    register: (
      email: string,
      password: string,
      fullName: string,
      role?: 'buyer' | 'seller',
    ) => AxPromise
    login: (email: string, password: string) => AxPromise
    getCurrentUser: (token: string) => AxPromise
    oauthGoogle: (
      idToken: string,
      opts?: { intended_role?: 'buyer' | 'seller'; full_name?: string },
    ) => AxPromise
    oauthApple: (
      idToken: string,
      opts?: { intended_role?: 'buyer' | 'seller'; full_name?: string },
    ) => AxPromise
    oauthFacebook: (
      accessToken: string,
      opts?: { intended_role?: 'buyer' | 'seller'; full_name?: string },
    ) => AxPromise
    getOAuthStatus: () => AxPromise
    forgotPassword: (email: string) => AxPromise
    resetPassword: (token: string, newPassword: string) => AxPromise
    setPassword: (
      token: string,
      payload: { new_password: string; current_password?: string },
    ) => AxPromise
    redeemGuestListingLink: (token: string) => AxPromise
    deleteAccount: (token: string, password?: string) => AxPromise
  }
  listingsAPI: {
    createListing: (data: unknown, token: string) => AxPromise
    guestCreateListing: (data: {
      listing: unknown
      seller: { full_name: string; email?: string; phone?: string }
    }) => AxPromise
    updateListing: (listingId: number, data: unknown, token: string) => AxPromise
    setListingReservation: (
      listingId: number,
      data: { reserved: boolean; buyer_id?: number | null },
      token: string,
    ) => AxPromise
    deleteListing: (listingId: number, token: string) => AxPromise
    getPhoneModels: () => AxPromise
    resolveBasicPhoneModel: (label: string) => AxPromise
    resolveCustomPhoneModel: (label: string, isSmartphone?: boolean) => AxPromise
    getListings: (
      skip?: number,
      limit?: number,
      sort?: string,
      filters?: { q?: string; city?: string },
    ) => AxPromise
    getMyListings: (token: string, skip?: number, limit?: number) => AxPromise
    getListing: (id: number) => AxPromise
    getPurchaseQuote: (
      listingId: number,
      route: 'direct' | 'guarantee',
      shipping?: {
        shipping_provider?: string
        dest_city?: string
        dest_postal?: string
        locker_id?: string
        compare_shipping?: boolean
      },
    ) => AxPromise
    buyNow: (
      listingId: number,
      token: string,
      route?: 'direct' | 'guarantee',
      shipping?: CheckoutShippingPayload,
    ) => AxPromise
    peerContact: (listingId: number, token: string) => AxPromise
    listComments: (listingId: number, token?: string | null) => AxPromise
    postComment: (listingId: number, body: string, token: string) => AxPromise
    deleteComment: (listingId: number, commentId: number, token: string) => AxPromise
    reportListing: (
      listingId: number,
      payload: { reason: string; detail?: string },
      token: string,
    ) => AxPromise
  }
  shippingAPI: {
    getProviders: () => AxPromise
    searchPlaces: (params: {
      q: string
      country?: string
      limit?: number
    }) => AxPromise
    reverseGeocode: (params: { lat: number; lon: number }) => AxPromise
    getLockers: (params?: {
      provider?: string
      city?: string
      q?: string
      country?: string
      lat?: number
      lon?: number
      limit?: number
    }) => AxPromise
    getQuote: (params: {
      provider?: string
      delivery_type?: string
      origin_city?: string
      origin_postal?: string
      dest_city?: string
      dest_postal?: string
      locker_id?: string
      listing_id?: number
      legs?: number
      compare_all?: boolean
    }) => AxPromise
  }
  offersAPI: {
    createOffer: (data: unknown, token: string) => AxPromise
    getOffer: (offerId: number, token: string) => AxPromise
    getOffersForListing: (listingId: number, token: string) => AxPromise
    acceptOffer: (offerId: number, token: string) => AxPromise
    rejectOffer: (offerId: number, token: string, feedback?: string) => AxPromise
    getMyOffers: (token: string, skip?: number, limit?: number) => AxPromise
    listOfferConversations: (token: string, skip?: number, limit?: number) => AxPromise
    listOfferMessages: (offerId: number, token: string) => AxPromise
    sendOfferMessage: (offerId: number, body: string, token: string) => AxPromise
  }
  paymentsAPI: {
    getPublicConfig: () => AxPromise
    getMyTransactions: (token: string) => AxPromise
    createTransaction: (offerId: number, token: string) => AxPromise
    createStripeCheckout: (
      transactionId: number,
      token: string,
      shipping?: CheckoutShippingPayload,
    ) => AxPromise
    confirmCheckout: (sessionId: string, token: string) => AxPromise
    cartCheckout: (
      listingIds: number[],
      token: string,
      opts?: { route?: 'direct' | 'guarantee'; shipping?: CheckoutShippingPayload },
    ) => AxPromise
    markShipped: (
      transactionId: number,
      token: string,
      payload?: { tracking_number?: string; carrier?: string },
    ) => AxPromise
    confirmDelivery: (transactionId: number, token: string) => AxPromise
    exchangePassCheckout: (token: string) => AxPromise
    exchangePassStatus: (token: string) => AxPromise
    cancelTransaction: (transactionId: number, reason: string, token: string) => AxPromise
    rateTransaction: (
      transactionId: number,
      body: { stars: number; comment?: string },
      token: string,
    ) => AxPromise
    getUserRatingSummary: (userId: number) => AxPromise
    getPlatformTrustSummary: () => AxPromise
    openDispute: (transactionId: number, reason: string, token: string) => AxPromise
  }
  addressesAPI: {
    list: (token: string) => AxPromise
    getDefault: (token: string) => AxPromise
    create: (data: Record<string, unknown>, token: string) => AxPromise
    update: (addressId: number, data: Record<string, unknown>, token: string) => AxPromise
    remove: (addressId: number, token: string) => AxPromise
  }
  pricingAPI: {
    suggest: (body: PricingSuggestBody) => AxPromise
    assessListingPhoto: (
      token: string,
      file: MultipartPhotoFile,
      opts?: AssessPhotoOptions,
    ) => AxPromise
    suggestWithPhotoPreview: (
      token: string,
      params: PricingSuggestPhotoParams,
      file: MultipartPhotoFile,
    ) => AxPromise
  }
  adminAPI: {
    overview: (token: string) => AxPromise
    listUsers: (
      token: string,
      params?: {
        q?: string
        filter?: '' | 'active' | 'blocked' | 'sellers' | 'pending_verification'
        skip?: number
        limit?: number
      },
    ) => AxPromise
    getUser: (token: string, userId: number) => AxPromise
    userListings: (
      token: string,
      userId: number,
      params?: { sold?: 'all' | 'active' | 'sold'; skip?: number; limit?: number },
    ) => AxPromise
    updateUser: (token: string, userId: number, body: { is_active: boolean }) => AxPromise
    phoneModelBrands: (
      token: string,
      params?: { kind?: 'smart' | 'basic'; q?: string },
    ) => AxPromise
    listPhoneModels: (
      token: string,
      params?: {
        q?: string
        manufacturer?: string
        kind?: 'smart' | 'basic'
        skip?: number
        limit?: number
      },
    ) => AxPromise
    createPhoneModel: (
      token: string,
      body: {
        manufacturer: string
        model_name: string
        category?: string | null
        description?: string | null
        release_year?: number | null
        storage_gb?: number[]
        suggested_price_eur?: number | null
      },
    ) => AxPromise
    updatePhoneModel: (
      token: string,
      modelId: number,
      body: {
        manufacturer?: string
        model_name?: string
        category?: string | null
        description?: string | null
        release_year?: number | null
        storage_gb?: number[]
        suggested_price_eur?: number | null
      },
    ) => AxPromise
    deletePhoneModel: (token: string, modelId: number) => AxPromise
    earningsSummary: (token: string) => AxPromise
    imeiSummary: (token: string) => AxPromise
    imeiChecks: (
      token: string,
      params?: {
        q?: string
        service?: string
        stolen_only?: boolean
        date_from?: string
        date_to?: string
        skip?: number
        limit?: number
      },
    ) => AxPromise
    imeiAnalytics: (token: string, params?: { days?: number }) => AxPromise
    badges: (token: string) => AxPromise
    trends: (token: string, params?: { days?: number }) => AxPromise
    activityFeed: (token: string, params?: { minutes?: number; limit?: number }) => AxPromise
    health: (token: string) => AxPromise
    search: (token: string, params?: { q?: string; limit?: number }) => AxPromise
    listListings: (
      token: string,
      params?: {
        q?: string
        status?: 'active' | 'sold' | 'all'
        filter?: '' | 'above_ai' | 'no_photos' | 'flagged' | 'hidden'
        skip?: number
        limit?: number
      },
    ) => AxPromise
    getListing: (token: string, listingId: number) => AxPromise
    updateListing: (
      token: string,
      listingId: number,
      body: { is_hidden?: boolean; is_flagged_suspicious?: boolean },
    ) => AxPromise
    bulkUpdateListings: (
      token: string,
      body: { listing_ids: number[]; is_hidden?: boolean; is_flagged_suspicious?: boolean },
    ) => AxPromise
    listTransactions: (
      token: string,
      params?: { q?: string; status?: string; skip?: number; limit?: number },
    ) => AxPromise
    auditLog: (
      token: string,
      params?: { q?: string; skip?: number; limit?: number },
    ) => AxPromise
    getTransaction: (token: string, transactionId: number) => AxPromise
    stuckTransactions: (token: string, params?: { limit?: number }) => AxPromise
    transactionHistory: (token: string, transactionId: number) => AxPromise
    refundTransaction: (token: string, transactionId: number, body?: { reason?: string }) => AxPromise
    resolveDispute: (
      token: string,
      transactionId: number,
      body: { outcome: 'refund' | 'complete'; reason?: string },
    ) => AxPromise
    pendingVerifications: (token: string) => AxPromise
    approveVerification: (token: string, userId: number) => AxPromise
    rejectVerification: (token: string, userId: number, body?: { note?: string }) => AxPromise
    userImeiChecks: (token: string, userId: number, params?: { limit?: number }) => AxPromise
    userTransactions: (token: string, userId: number, params?: { limit?: number }) => AxPromise
    userActivityLog: (token: string, userId: number, params?: { limit?: number }) => AxPromise
    phoneModelQuality: (token: string) => AxPromise
    backfillPhoneModels: (token: string, body?: { overwrite?: boolean }) => AxPromise
    metrics: (token: string) => AxPromise
  }
  imeiAPI: {
    getStats: () => AxPromise
    getCatalog: () => AxPromise
    createCheckCheckout: (
      imei: string,
      service: string,
      token?: string | null,
      client?: 'mobile' | 'web',
      locale?: string | null,
    ) => AxPromise
    check: (
      imei: string,
      token?: string | null,
      service?: string,
      checkoutSessionId?: string | null,
    ) => AxPromise
    listReports: (token: string) => AxPromise
    claimReport: (
      token: string,
      body: {
        imei: string
        service?: string
        is_valid: boolean
        is_stolen?: boolean | null
        brand?: string | null
        model?: string | null
        checked_at?: string
      },
    ) => AxPromise
    checkWarranty: (imei: string) => AxPromise
    /** Label/box photo → OCR (server needs OPENAI_API_KEY by default). Caller builds `FormData` with `file`. */
    ocrExtractFromImage: (token: string, formData: FormData) => AxPromise
    /** Label photo → OCR → IMEI verify + listing_hints (preferred seller step 1). */
    scanLabelAndCheck: (token: string, formData: FormData) => AxPromise
  }
  stripeConnectAPI: {
    getStatus: (token: string) => AxPromise
    createOnboardingLink: (token: string) => AxPromise
  }
  sellerVerificationAPI: {
    getStatus: (token: string) => AxPromise
    getCities: () => AxPromise
    saveProfile: (token: string, body: Record<string, unknown>) => AxPromise
    apply: (token: string, body: Record<string, unknown>) => AxPromise
  }
  notificationsAPI: {
    list: (token: string, skip?: number, limit?: number) => AxPromise
    unreadCount: (token: string) => AxPromise
    markAllRead: (token: string) => AxPromise
    markOneRead: (notificationId: number, token: string) => AxPromise
  }
  aiAssistantAPI: {
    status: () => AxPromise
    chat: (
      messages: Array<{ role: 'user' | 'assistant'; content: string }>,
      token?: string | null,
    ) => AxPromise
  }
}

/**
 * Single HTTP layer for every reDevicer client (web, iOS, Android).
 * Platform-specific code (browser File uploads, etc.) should wrap `axios` or extend exports in the app.
 */
export function createReDevicerApi(config: ReDevicerApiConfig): ReDevicerApi {
  const apiClient = axios.create({
    baseURL: config.baseURL,
  })

  const authAPI: ReDevicerApi['authAPI'] = {
    register: (email, password, fullName, role) =>
      apiClient.post('/auth/register', {
        email,
        password,
        full_name: fullName,
        ...(role ? { role } : {}),
      }),
    login: (email, password) => apiClient.post('/auth/login', { email, password }),
    getCurrentUser: (token) =>
      apiClient.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } }),
    oauthGoogle: (idToken, opts) =>
      apiClient.post('/auth/oauth/google', {
        id_token: idToken,
        ...(opts?.intended_role ? { intended_role: opts.intended_role } : {}),
        ...(opts?.full_name ? { full_name: opts.full_name } : {}),
      }),
    oauthApple: (idToken, opts) =>
      apiClient.post('/auth/oauth/apple', {
        id_token: idToken,
        ...(opts?.intended_role ? { intended_role: opts.intended_role } : {}),
        ...(opts?.full_name ? { full_name: opts.full_name } : {}),
      }),
    oauthFacebook: (accessToken, opts) =>
      apiClient.post('/auth/oauth/facebook', {
        access_token: accessToken,
        ...(opts?.intended_role ? { intended_role: opts.intended_role } : {}),
        ...(opts?.full_name ? { full_name: opts.full_name } : {}),
      }),
    getOAuthStatus: () => apiClient.get('/auth/oauth/status'),
    forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
    resetPassword: (token, newPassword) =>
      apiClient.post('/auth/reset-password', { token, new_password: newPassword }),
    setPassword: (token, payload) =>
      apiClient.post('/auth/set-password', payload, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    redeemGuestListingLink: (token) =>
      apiClient.post('/auth/guest-listing-link', { token }),
    deleteAccount: (token, password) =>
      apiClient.post(
        '/auth/me/delete',
        { ...(password ? { password } : {}) },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
  }

  const listingsAPI: ReDevicerApi['listingsAPI'] = {
    createListing: (data, token) =>
      apiClient.post('/listings/', data, { headers: { Authorization: `Bearer ${token}` } }),
    guestCreateListing: (data) => apiClient.post('/listings/guest-create', data),
    updateListing: (listingId, data, token) =>
      apiClient.patch(`/listings/${listingId}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    setListingReservation: (listingId, data, token) =>
      apiClient.patch(`/listings/${listingId}/reservation`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    deleteListing: (listingId, token) =>
      apiClient.delete(`/listings/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    getPhoneModels: () => apiClient.get('/listings/phone-models'),
    resolveBasicPhoneModel: (label: string) =>
      apiClient.post('/listings/phone-models/resolve-basic', { label }),
    resolveCustomPhoneModel: (label: string, isSmartphone = true) =>
      apiClient.post('/listings/phone-models/resolve-custom', {
        label,
        is_smartphone: isSmartphone,
      }),
    getListings: (skip = 0, limit = 20, sort?: string, filters?: { q?: string; city?: string }) =>
      apiClient.get('/listings/', {
        params: {
          skip,
          limit,
          ...(sort ? { sort } : {}),
          ...(filters?.q?.trim() ? { q: filters.q.trim() } : {}),
          ...(filters?.city?.trim() ? { city: filters.city.trim() } : {}),
        },
      }),
    getMyListings: (token, skip = 0, limit = 20) =>
      apiClient.get('/listings/mine', {
        params: { skip, limit },
        headers: { Authorization: `Bearer ${token}` },
      }),
    getListing: (id) => apiClient.get(`/listings/${id}`),
    getPurchaseQuote: (listingId, route, shipping) =>
      apiClient.get(`/listings/${listingId}/purchase-quote`, {
        params: {
          route,
          ...(shipping?.shipping_provider ? { shipping_provider: shipping.shipping_provider } : {}),
          ...(shipping?.dest_city ? { dest_city: shipping.dest_city } : {}),
          ...(shipping?.dest_postal ? { dest_postal: shipping.dest_postal } : {}),
          ...(shipping?.locker_id ? { locker_id: shipping.locker_id } : {}),
          ...(shipping?.compare_shipping ? { compare_shipping: true } : {}),
        },
      }),
    buyNow: (listingId, token, route = 'direct', shipping) =>
      apiClient.post(
        `/listings/${listingId}/buy-now`,
        { route, ...(shipping ?? {}) },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    peerContact: (listingId, token) =>
      apiClient.post(
        `/listings/${listingId}/peer-contact`,
        { accept_disclaimer: true },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    listComments: (listingId, token) =>
      apiClient.get(`/listings/${listingId}/comments`, {
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      }),
    postComment: (listingId, body, token) =>
      apiClient.post(
        `/listings/${listingId}/comments`,
        { body },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    deleteComment: (listingId, commentId, token) =>
      apiClient.delete(`/listings/${listingId}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    reportListing: (listingId, payload, token) =>
      apiClient.post(`/listings/${listingId}/report`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      }),
  }

  const offersAPI: ReDevicerApi['offersAPI'] = {
    createOffer: (data, token) =>
      apiClient.post('/offers/', data, { headers: { Authorization: `Bearer ${token}` } }),
    getOffer: (offerId, token) =>
      apiClient.get(`/offers/${offerId}`, { headers: { Authorization: `Bearer ${token}` } }),
    getOffersForListing: (listingId, token) =>
      apiClient.get(`/offers/listing/${listingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    acceptOffer: (offerId, token) =>
      apiClient.post(`/offers/${offerId}/accept`, {}, { headers: { Authorization: `Bearer ${token}` } }),
    rejectOffer: (offerId, token, feedback) =>
      apiClient.post(
        `/offers/${offerId}/reject`,
        feedback?.trim() ? { feedback: feedback.trim() } : {},
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    getMyOffers: (token, skip = 0, limit = 50) =>
      apiClient.get('/offers/mine', {
        params: { skip, limit },
        headers: { Authorization: `Bearer ${token}` },
      }),
    listOfferConversations: (token, skip = 0, limit = 50) =>
      apiClient.get('/offers/conversations', {
        params: { skip, limit },
        headers: { Authorization: `Bearer ${token}` },
      }),
    listOfferMessages: (offerId, token) =>
      apiClient.get(`/offers/${offerId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    sendOfferMessage: (offerId, body, token) =>
      apiClient.post(
        `/offers/${offerId}/messages`,
        { body },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
  }

  const paymentsAPI: ReDevicerApi['paymentsAPI'] = {
    getPublicConfig: () => apiClient.get('/payments/public-config'),
    getMyTransactions: (token) =>
      apiClient.get('/payments/transactions/mine', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    createTransaction: (offerId, token) =>
      apiClient.post(
        '/payments/transactions',
        { offer_id: offerId, payment_method: 'stripe' },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    createStripeCheckout: (transactionId, token, shipping) =>
      apiClient.post(
        `/payments/transactions/${transactionId}/stripe-checkout`,
        shipping ?? {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      ),
    confirmCheckout: (sessionId, token) =>
      apiClient.post(
        '/payments/confirm-checkout',
        { session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    cartCheckout: (listingIds, token, opts) =>
      apiClient.post(
        '/payments/cart-checkout',
        {
          listing_ids: listingIds,
          route: opts?.route ?? 'direct',
          ...(opts?.shipping ?? {}),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    markShipped: (transactionId, token, payload) =>
      apiClient.post(`/payments/transactions/${transactionId}/mark-shipped`, payload ?? {}, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    confirmDelivery: (transactionId, token) =>
      apiClient.post(
        `/payments/transactions/${transactionId}/confirm-delivery`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    exchangePassCheckout: (token) =>
      apiClient.post(
        '/payments/exchange-pass/checkout',
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    exchangePassStatus: (token) =>
      apiClient.get('/payments/exchange-pass/status', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    cancelTransaction: (transactionId, reason, token) =>
      apiClient.post(
        `/payments/transactions/${transactionId}/cancel`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
    rateTransaction: (transactionId, body, token) =>
      apiClient.post(`/payments/transactions/${transactionId}/rate`, body, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    getUserRatingSummary: (userId) => apiClient.get(`/payments/users/${userId}/rating-summary`),
    getPlatformTrustSummary: () => apiClient.get('/payments/platform-trust-summary'),
    openDispute: (transactionId, reason, token) =>
      apiClient.post(
        `/payments/transactions/${transactionId}/dispute`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      ),
  }

  const addressesAPI: ReDevicerApi['addressesAPI'] = {
    list: (token) =>
      apiClient.get('/addresses', { headers: { Authorization: `Bearer ${token}` } }),
    getDefault: (token) =>
      apiClient.get('/addresses/default', { headers: { Authorization: `Bearer ${token}` } }),
    create: (data, token) =>
      apiClient.post('/addresses', data, { headers: { Authorization: `Bearer ${token}` } }),
    update: (addressId, data, token) =>
      apiClient.put(`/addresses/${addressId}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    remove: (addressId, token) =>
      apiClient.delete(`/addresses/${addressId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
  }

  const pricingAPI: ReDevicerApi['pricingAPI'] = {
    suggest: (body) => postPricingSuggestResilient(apiClient, body),
    assessListingPhoto: (token, file, opts) =>
      apiClient.post('/pricing/assess-photo', buildAssessPhotoFormData(file, opts), {
        headers: { Authorization: `Bearer ${token}` },
      }),
    suggestWithPhotoPreview: (token, params, file) =>
      postPricingSuggestPhotoResilient(
        apiClient,
        params,
        (p) => buildSuggestWithPhotoFormData(p, file),
        { headers: { Authorization: `Bearer ${token}` } },
      ),
  }

  const adminHeaders = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } })

  const adminAPI: ReDevicerApi['adminAPI'] = {
    overview: (token) => apiClient.get('/admin/overview', adminHeaders(token)),
    listUsers: (token, params) =>
      apiClient.get('/admin/users', { ...adminHeaders(token), params }),
    getUser: (token, userId) => apiClient.get(`/admin/users/${userId}`, adminHeaders(token)),
    userListings: (token, userId, params) =>
      apiClient.get(`/admin/users/${userId}/listings`, { ...adminHeaders(token), params }),
    updateUser: (token, userId, body) =>
      apiClient.patch(`/admin/users/${userId}`, body, adminHeaders(token)),
    phoneModelBrands: (token, params) =>
      apiClient.get('/admin/phone-models/brands', { ...adminHeaders(token), params }),
    listPhoneModels: (token, params) =>
      apiClient.get('/admin/phone-models', { ...adminHeaders(token), params }),
    createPhoneModel: (token, body) =>
      apiClient.post('/admin/phone-models', body, adminHeaders(token)),
    updatePhoneModel: (token, modelId, body) =>
      apiClient.patch(`/admin/phone-models/${modelId}`, body, adminHeaders(token)),
    deletePhoneModel: (token, modelId) =>
      apiClient.delete(`/admin/phone-models/${modelId}`, adminHeaders(token)),
    earningsSummary: (token) =>
      apiClient.get('/admin/earnings/summary', adminHeaders(token)),
    imeiSummary: (token) => apiClient.get('/admin/imei/summary', adminHeaders(token)),
    imeiChecks: (token, params) =>
      apiClient.get('/admin/imei/checks', { ...adminHeaders(token), params }),
    imeiAnalytics: (token, params) =>
      apiClient.get('/admin/imei/analytics', { ...adminHeaders(token), params }),
    badges: (token) => apiClient.get('/admin/badges', adminHeaders(token)),
    trends: (token, params) => apiClient.get('/admin/trends', { ...adminHeaders(token), params }),
    activityFeed: (token, params) =>
      apiClient.get('/admin/activity-feed', { ...adminHeaders(token), params }),
    health: (token) => apiClient.get('/admin/health', adminHeaders(token)),
    search: (token, params) => apiClient.get('/admin/search', { ...adminHeaders(token), params }),
    listListings: (token, params) =>
      apiClient.get('/admin/listings', { ...adminHeaders(token), params }),
    getListing: (token, listingId) =>
      apiClient.get(`/admin/listings/${listingId}`, adminHeaders(token)),
    updateListing: (token, listingId, body) =>
      apiClient.patch(`/admin/listings/${listingId}`, body, adminHeaders(token)),
    bulkUpdateListings: (token, body) =>
      apiClient.post('/admin/listings/bulk', body, adminHeaders(token)),
    listTransactions: (token, params) =>
      apiClient.get('/admin/transactions', { ...adminHeaders(token), params }),
    auditLog: (token, params) =>
      apiClient.get('/admin/audit-log', { ...adminHeaders(token), params }),
    getTransaction: (token, transactionId) =>
      apiClient.get(`/admin/transactions/${transactionId}`, adminHeaders(token)),
    stuckTransactions: (token, params) =>
      apiClient.get('/admin/transactions/stuck', { ...adminHeaders(token), params }),
    transactionHistory: (token, transactionId) =>
      apiClient.get(`/admin/transactions/${transactionId}/history`, adminHeaders(token)),
    refundTransaction: (token, transactionId, body) =>
      apiClient.post(`/admin/transactions/${transactionId}/refund`, body ?? {}, adminHeaders(token)),
    resolveDispute: (token, transactionId, body) =>
      apiClient.post(`/admin/transactions/${transactionId}/dispute/resolve`, body, adminHeaders(token)),
    pendingVerifications: (token) =>
      apiClient.get('/admin/seller-verifications/pending', adminHeaders(token)),
    approveVerification: (token, userId) =>
      apiClient.post(`/admin/seller-verifications/${userId}/approve`, {}, adminHeaders(token)),
    rejectVerification: (token, userId, body) =>
      apiClient.post(`/admin/seller-verifications/${userId}/reject`, body ?? {}, adminHeaders(token)),
    userImeiChecks: (token, userId, params) =>
      apiClient.get(`/admin/users/${userId}/imei-checks`, { ...adminHeaders(token), params }),
    userTransactions: (token, userId, params) =>
      apiClient.get(`/admin/users/${userId}/transactions`, { ...adminHeaders(token), params }),
    userActivityLog: (token, userId, params) =>
      apiClient.get(`/admin/users/${userId}/activity-log`, { ...adminHeaders(token), params }),
    phoneModelQuality: (token) =>
      apiClient.get('/admin/phone-models/quality', adminHeaders(token)),
    backfillPhoneModels: (token, body) =>
      apiClient.post('/admin/phone-models/backfill', body ?? {}, adminHeaders(token)),
    metrics: (token) => apiClient.get('/admin/metrics', adminHeaders(token)),
  }

  const imeiAPI: ReDevicerApi['imeiAPI'] = {
    getStats: () => apiClient.get('/imei/stats'),
    getCatalog: () => apiClient.get('/imei/catalog'),
    createCheckCheckout: (imei, service, token, client, locale) =>
      apiClient.post(
        '/imei/check/checkout',
        {
          imei,
          service,
          ...(client === 'mobile' ? { client: 'mobile' } : client === 'web' ? { client: 'web' } : {}),
          ...(locale ? { locale: locale.slice(0, 5) } : {}),
        },
        { ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}) },
      ),
    check: (imei, token, service, checkoutSessionId) =>
      apiClient.post(
        '/imei/check',
        {
          imei,
          ...(service ? { service } : {}),
          ...(checkoutSessionId ? { checkout_session_id: checkoutSessionId } : {}),
        },
        {
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
          timeout: 90000,
        },
      ),
    listReports: (token) =>
      apiClient.get('/imei/reports', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    claimReport: (token, body) =>
      apiClient.post('/imei/reports/claim', body, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    checkWarranty: (imei) => apiClient.post('/imei/warranty', { imei }),
    ocrExtractFromImage: (token, formData) =>
      apiClient.post('/imei/ocr-extract', formData, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    scanLabelAndCheck: (token, formData) =>
      apiClient.post('/imei/scan-label', formData, {
        headers: { Authorization: `Bearer ${token}` },
      }),
  }

  const stripeConnectAPI: ReDevicerApi['stripeConnectAPI'] = {
    getStatus: (token) =>
      apiClient.get('/stripe-connect/status', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    createOnboardingLink: (token) =>
      apiClient.post(
        '/stripe-connect/onboarding-link',
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      ),
  }

  const sellerVerificationAPI: ReDevicerApi['sellerVerificationAPI'] = {
    getStatus: (token) =>
      apiClient.get('/seller-verification/status', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    getCities: () => apiClient.get('/seller-verification/cities'),
    saveProfile: (token, body) =>
      apiClient.post('/seller-verification/profile', body, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    apply: (token, body) =>
      apiClient.post('/seller-verification/apply', body, {
        headers: { Authorization: `Bearer ${token}` },
      }),
  }

  const aiAssistantAPI: ReDevicerApi['aiAssistantAPI'] = {
    status: () => apiClient.get('/ai-assistant/status'),
    chat: (messages, token) =>
      apiClient.post(
        '/ai-assistant/chat',
        { messages },
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      ),
  }

  const notificationsAPI: ReDevicerApi['notificationsAPI'] = {
    list: (token, skip = 0, limit = 50) =>
      apiClient.get('/notifications/', {
        params: { skip, limit },
        headers: { Authorization: `Bearer ${token}` },
      }),
    unreadCount: (token) =>
      apiClient.get('/notifications/unread-count', {
        headers: { Authorization: `Bearer ${token}` },
      }),
    markAllRead: (token) =>
      apiClient.post('/notifications/mark-read', {}, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    markOneRead: (notificationId, token) =>
      apiClient.post(`/notifications/${notificationId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      }),
  }

  const shippingAPI: ReDevicerApi['shippingAPI'] = {
    getProviders: () => apiClient.get('/shipping/providers'),
    searchPlaces: (params) => apiClient.get('/shipping/places/search', { params }),
    reverseGeocode: (params) => apiClient.get('/shipping/places/reverse', { params }),
    getLockers: (params) => apiClient.get('/shipping/lockers', { params }),
    getQuote: (params) => apiClient.get('/shipping/quote', { params }),
  }

  return {
    axios: apiClient,
    authAPI,
    listingsAPI,
    offersAPI,
    paymentsAPI,
    pricingAPI,
    adminAPI,
    imeiAPI,
    stripeConnectAPI,
    sellerVerificationAPI,
    notificationsAPI,
    aiAssistantAPI,
    shippingAPI,
    addressesAPI,
  }
}
